import { useMemo, useState } from "react";
import { Wallet } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, Segmented, StatCard, InfoRow, compactINR, inr } from "@/components/tools/kit";
import { GrowthArea } from "@/components/tools/charts";
import { ExportButtons } from "@/components/tools/ExportButtons";
import { fd } from "@/lib/calculators";

export default function Fd() {
  const [principal, setPrincipal] = useState(500000);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(5);
  const [payout, setPayout] = useState("cumulative");
  const [compounding, setCompounding] = useState("quarterly");
  const [senior, setSenior] = useState("no");
  const isSenior = senior === "yes";
  const r = useMemo(() => fd({ principal, rate, years, compounding, payout, senior: isSenior }), [principal, rate, years, compounding, payout, isSenior]);
  const isCum = payout === "cumulative";

  const buildPayload = () => ({
    title: "My Finanz Planner FD Report",
    subtitle: `${isCum ? "Cumulative" : "Payout"} Fixed Deposit`,
    meta: [
      { label: "Principal", value: inr(principal) },
      { label: "Interest Rate", value: `${r.rate}% p.a.${isSenior ? " (incl. senior +0.5%)" : ""}` },
      { label: "Tenure", value: `${years} years` },
      { label: "Type", value: isCum ? `Cumulative (${compounding})` : `Payout (${payout})` },
      { label: "Interest Earned", value: inr(r.interest) },
      { label: "Maturity Value", value: inr(isCum ? r.maturity : principal) },
    ],
    sheets: isCum
      ? [{ name: "Year-wise", headers: ["Year", "Value"], rows: r.points.map((p) => [p.year, Math.round(p.value)]) }]
      : [{ name: "Payout", headers: ["Metric", "Value"], rows: [["Periodic payout", Math.round(r.periodicInterest)], ["Payouts / year", r.freq], ["Total interest", Math.round(r.interest)]] }],
  });

  return (
    <ToolLayout title="FD Calculator" subtitle="Cumulative or regular-payout fixed deposits" icon={Wallet} accent="#D99B26">
      <TwoCol
        left={
          <SectionCard title="Deposit details">
            <div className="space-y-5">
              <Field label="Deposit amount"><MoneyInput testid="fd-principal" value={principal} onChange={setPrincipal} helper={compactINR(principal)} /></Field>
              <Field label="Interest rate (p.a.)" hint={rate + "%"}><SliderRow testid="fd-rate" value={rate} min={3} max={12} step={0.05} suffix="%" onChange={setRate} /></Field>
              <Field label="Tenure" hint={years + " yrs"}><SliderRow testid="fd-years" value={years} min={1} max={20} suffix=" yr" onChange={setYears} /></Field>
              <Field label="Interest type">
                <Segmented testid="fd-payout" value={payout} onChange={setPayout} layoutId="fd-payout-pill"
                  options={[{ id: "cumulative", label: "Cumulative" }, { id: "monthly", label: "Monthly" }, { id: "quarterly", label: "Quarterly" }, { id: "yearly", label: "Yearly" }]} />
              </Field>
              {isCum && (
                <Field label="Compounding frequency">
                  <Segmented testid="fd-compounding" value={compounding} onChange={setCompounding} layoutId="fd-comp-pill"
                    options={[{ id: "quarterly", label: "Quarterly" }, { id: "monthly", label: "Monthly" }, { id: "yearly", label: "Yearly" }]} />
                </Field>
              )}
              <Field label="Senior citizen (+0.5%)">
                <Segmented testid="fd-senior" value={senior} onChange={setSenior} layoutId="fd-senior-pill"
                  options={[{ id: "no", label: "No" }, { id: "yes", label: "Yes" }]} />
              </Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="fd-principal-stat" label="Principal" value={compactINR(principal)} accent="#2A6F7F" />
              <StatCard testid="fd-interest" label="Interest Earned" value={compactINR(r.interest)} accent="#D99B26" />
              <StatCard testid="fd-maturity" label={isCum ? "Maturity Value" : "Principal Returned"} value={compactINR(isCum ? r.maturity : principal)} accent="#2D6A4F" big />
            </div>
            {isCum ? (
              <SectionCard title="Value over time"><GrowthArea data={r.points} keys={[{ key: "value", name: "FD Value", color: "#D99B26" }]} /></SectionCard>
            ) : (
              <SectionCard title="Regular payout">
                <InfoRow label={`Interest paid ${payout}`} value={inr(r.periodicInterest)} strong />
                <InfoRow label="Payouts per year" value={r.freq} />
                <InfoRow label="Total interest over tenure" value={inr(r.interest)} />
                <InfoRow label="Principal returned at maturity" value={inr(principal)} />
              </SectionCard>
            )}
            <ExportButtons buildPayload={buildPayload} />
          </>
        }
      />
    </ToolLayout>
  );
}
